import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow, format } from "date-fns";
import { supabase } from "../lib/supabase";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "N/A";
  try {
    return format(new Date(date), "MMM d, yyyy");
  } catch (error) {
    console.warn("Invalid date format:", date);
    return "N/A";
  }
}

export function formatTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "h:mm a");
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMM d, yyyy 'at' h:mm a");
}

export function timeAgo(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}

export function truncate(str: string, length: number): string {
  if (!str) return "";
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

export function formatReportCategory(category: string): string {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "resolved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "invalid":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getEntityTypeLabel(type: string): string {
  switch (type.toLowerCase()) {
    case "user":
      return "User";
    case "citizen":
      return "Citizen";
    case "rating":
      return "Rating";
    case "comment":
      return "Comment";
    default:
      return type;
  }
}

export const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const handleCitizenImageUpload = async (
  file: File,
  type: "profile" | "main" | "banner",
  slug: string
): Promise<string> => {
  // Size limits: banner (3MB), main (3MB), profile (2MB)
  const maxSize = type === "profile" ? 2 * 1024 * 1024 : 3 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(
      `Image size should not exceed ${maxSize / (1024 * 1024)}MB`
    );
  }

  // Check file type
  const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Only JPEG, JPG and PNG files are allowed");
  }

  // Dimension checks
  const dimensions = await getImageDimensions(file);
  const dimensionLimits = {
    banner: { width: 1920, height: 400 },
    main: { width: 1200, height: Infinity }, // Allow any height for main image
    profile: { width: 500, height: 500 },
  };

  if (type === "main") {
    // For main image, only check width
    if (dimensions.width > dimensionLimits.main.width) {
      throw new Error(
        `Main image width should not exceed ${dimensionLimits.main.width} pixels`
      );
    }
  } else {
    // For other types, check both dimensions
    if (
      dimensions.width > dimensionLimits[type].width ||
      dimensions.height > dimensionLimits[type].height
    ) {
      throw new Error(
        `${type} image dimensions should not exceed ${dimensionLimits[type].width}x${dimensionLimits[type].height} pixels`
      );
    }
  }

  // Get the current session
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error("Authentication required");
  }

  const fileExt = file.type.split("/").pop();
  const fileName = `${slug}-${type}-pic.${fileExt}`;
  const filePath = `public/citizen-images/${fileName}`;

  // Upload with authenticated client
  const { data, error: uploadError } = await supabase.storage
    .from("ratecitizen-web-app")
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: true,
      contentType: file.type, // Explicitly set content type
    });

  if (uploadError) {
    console.error("Upload error:", uploadError); // Add detailed error logging
    throw new Error(`Upload failed: ${uploadError.message}`);
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from("ratecitizen-web-app").getPublicUrl(filePath);

  return publicUrl;
};

/**
 * Downloads an external image and re-uploads it to Supabase storage
 * @param imageUrl The external image URL
 * @param type The type of citizen image (profile, main, banner)
 * @param slug The citizen slug for file naming
 * @returns The new Supabase public URL or the original URL if the process fails
 */
export const downloadAndUploadExternalImage = async (
  imageUrl: string,
  type: "profile" | "main" | "banner",
  slug: string
): Promise<string> => {
  if (!imageUrl || typeof imageUrl !== "string") {
    return imageUrl;
  }

  // Validate URL format
  try {
    new URL(imageUrl);
  } catch (error) {
    console.warn(`Invalid URL format: ${imageUrl}`);
    return imageUrl;
  }

  // Check if it's an image URL with allowed extension
  const urlLower = imageUrl.toLowerCase();
  if (
    !urlLower.endsWith(".jpg") &&
    !urlLower.endsWith(".jpeg") &&
    !urlLower.endsWith(".png")
  ) {
    console.warn(`URL doesn't have an image extension: ${imageUrl}`);
    return imageUrl;
  }

  try {
    // Get file extension from URL
    const fileExt = urlLower.split(".").pop() || "jpg";

    // Step 2: Download the image with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(imageUrl, {
        signal: controller.signal,
        headers: {
          // Add common headers to help with some CDNs and image servers
          "User-Agent": "Mozilla/5.0 (compatible; AdminPanel/1.0)",
          Accept: "image/jpeg,image/png,image/*",
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to download image: ${response.status} ${response.statusText}`
        );
      }

      // Check content type
      const contentType = response.headers.get("content-type");
      if (
        !contentType ||
        !["image/jpeg", "image/png", "image/jpg"].includes(contentType)
      ) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      // Get blob from response
      const blob = await response.blob();

      // Size check (3MB for main/banner, 2MB for profile)
      const maxSize = type === "profile" ? 2 * 1024 * 1024 : 3 * 1024 * 1024;
      if (blob.size > maxSize) {
        throw new Error(`Image size exceeds ${maxSize / (1024 * 1024)}MB`);
      }

      // Get the current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error("Authentication required");
      }

      // Step 3 & 4: Upload to Supabase
      const fileName = `${slug}-${type}-pic.${fileExt}`;
      const filePath = `public/citizen-images/${fileName}`;

      const { data, error: uploadError } = await supabase.storage
        .from("ratecitizen-web-app")
        .upload(filePath, blob, {
          cacheControl: "3600",
          upsert: true,
          contentType: blob.type,
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Step 5: Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("ratecitizen-web-app").getPublicUrl(filePath);

      console.log(
        `Successfully uploaded external image to Supabase: ${publicUrl}`
      );
      return publicUrl;
    } catch (fetchError) {
      clearTimeout(timeoutId);

      if (fetchError.name === "AbortError") {
        throw new Error("Image download timed out after 10 seconds");
      }

      throw fetchError;
    }
  } catch (error) {
    // Step 6: Log error and return original URL if failed
    console.error(`Error processing external image: ${error.message}`);
    return imageUrl;
  }
};
